import { hasProductPackages } from '@/lib/package-utils'
import dayjs from 'dayjs'
import { getWeekRange, getLastCompleteDate } from '@/lib/date-utils'
import { getGlobalLatestStartDate } from '@/lib/dr-utils'
import {
  getDayRoasFieldName,
  getPrevDayRoasFieldName,
} from '@/components/roi/day-roas-utils'
import {
  WEEKLY_ROAS_DAYS,
  WEEKLY_VIEW_MODE_OPTIONS,
  buildWeeklyNormalModeVisibility,
  buildWeeklyRoasModeVisibility,
  getWeeklyDefaultColumnVisibility,
  isWeeklyRoasValueVisible,
} from './constants'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useColumnFilters } from '@/components/data-table-selectors'
import {
  useCountry,
  useCountry2,
  DropdownCountryCombined,
} from '@/components/DropdownCountry'
import { DropdownPid2, DropdownPid3, usePid2 } from '@/components/DropdownPid'
import DropdownWeekStart, { useWSStore } from '@/components/DropdownWeek'
import { MyDatePicker, useWeeklyDatePicker } from '@/components/MyDatePicker'
import {
  useReadableCost,
  useReadableEr,
  useReadableRevenue,
  useReadableRoas,
} from '@/hooks/auth'
import useSWR from 'swr'
import { create, useStore as useZustandStore } from 'zustand'
import {
  columnCPI,
  columnCPIInstalls,
  columnCosts,
  columnDate,
  columnEr,
  columnDnu,
  columnInstalls,
  columnProfit,
  columnRevenue,
  createAllWeeklyDayRoasColumnsWithTd3,
} from './columns'

import DropdownPackageName, {
  usePackageName,
} from '@/components/DropdownPackage'

import { useMemo, useState, useCallback } from 'react'
import { exportToCsv } from '@/lib/utils'
import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'
import { ChartCodeViewer } from '@/components/zthd/chart-code-viewer'
import { LineChart as LineChartIcon } from 'lucide-react'
import WeeklyChart, {
  getDefaultWeeklyChartMetrics,
} from './weekly-chart'
const useColumnVisibilityStore = createColumnVisibilityStore(
  getWeeklyDefaultColumnVisibility(),
  'weekly',
)

const WeekonWeek = () => {
  return (
    <>
      <Form />
      <RenderTable />
    </>
  )
}

const Form = () => {
  return (
    <div className="flex items-center gap-4 py-4 sm:gap-2 sm:py-2 flex-wrap xs:gap-1 xs:py-1">
      <DropdownPid2 />
      <DropdownPid3 />
      <DropdownPackageName />
      <DropdownCountryCombined />
      <MyDatePicker
        datePickerStore={useWeeklyDatePicker}
        getMaxDate="complete"
        showMaxDate={true}
      />
      <DropdownWeekStart />
    </div>
  )
}

const RenderTable = () => {
  const pids = usePid2((state) => state.pids)
  const country = useCountry((state) => state.value)
  const countries = useCountry2((state) => state.countries)
  const packageName = usePackageName((state) => state.value)
  const pid = pids.join(',')

  const readableCost = useReadableCost(pid, country)
  const readableRevenue = useReadableRevenue(pid, country)
  const readableEr = useReadableEr(pid, country)
  const readableRoas = useReadableRoas(pid, country)

  const { data: baselines } = useSWR([readableEr ? '/ers/baselines' : null])
  const erBaseLine = baselines?.[country]?.[pid] ?? 0.35
  const { data: drBaselines } = useSWR(['/drs/baselines'])
  const { data: costBaselines } = useSWR(['/costs/baselines'])
  const { data: profitBaselines } = useSWR(['/profits/baselines'])

  // 获取全局最新的 baseline 配置日期
  const latestBaselineDate = useMemo(() => {
    if (!drBaselines) return ''
    const dates = WEEKLY_ROAS_DAYS.map((d) =>
      getGlobalLatestStartDate(drBaselines, d),
    ).filter(Boolean)
    return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : ''
  }, [drBaselines])

  const startDate = useWeeklyDatePicker((state) => state.startDate)
  const endDateFromPicker = useWeeklyDatePicker((state) => state.endDate)
  const allowIncomplete = useWeeklyDatePicker((state) => state.allowIncomplete)

  // 计算完整数据的截止日期
  // 一般数据（costs, revenues, ers, dnu）：下午4点完整
  const lastCompleteDate = getLastCompleteDate(false)
  // ROAS数据：下午5点完整
  const lastCompleteDateForRoas = getLastCompleteDate(true)
  // 使用用户选择的endDate和完整数据截止日期中的较小值（如果不允许不完整数据）
  const endDate = allowIncomplete
    ? Number(endDateFromPicker)
    : Math.min(Number(endDateFromPicker), Number(lastCompleteDate))
  const endDateForRoas = allowIncomplete
    ? Number(endDateFromPicker)
    : Math.min(Number(endDateFromPicker), Number(lastCompleteDateForRoas))

  const ws = useZustandStore(useWSStore, (state) => state.value)

  const { columnVisibility, setColumnVisibility } = useColumnVisibilityStore()
  const columnFilters = useColumnFilters((state) => state.columnFilters)

  const [viewMode, setViewMode] = useState('normal')
  const [isChartOpen, setIsChartOpen] = useState(false)
  const [chartInitialMetrics, setChartInitialMetrics] = useState(() =>
    getDefaultWeeklyChartMetrics({
      readableRevenue: false,
      readableCost: false,
    }),
  )

  const availableMetrics = useMemo(() => {
    const metrics = ['dnu', 'er']
    if (readableCost) {
      metrics.push('installs', 'costs', 'cpi', 'cpiInstalls')
    }
    if (readableRevenue) {
      metrics.push('revenues')
    }
    if (readableRevenue && readableCost) {
      metrics.push('profit')
    }
    if (readableRoas) {
      WEEKLY_ROAS_DAYS.forEach((day) => {
        metrics.push(getDayRoasFieldName(day))
      })
    }
    return metrics
  }, [readableCost, readableRevenue, readableRoas])

  const openChartWithMetrics = useCallback(
    (metrics) => {
      const next = Array.isArray(metrics) ? metrics : [metrics]
      const sameSelection =
        isChartOpen &&
        next.length === chartInitialMetrics.length &&
        next.every((m) => chartInitialMetrics.includes(m))
      if (!sameSelection) {
        setChartInitialMetrics(next)
      }
      setIsChartOpen(true)
    },
    [isChartOpen, chartInitialMetrics],
  )

  const handleRoasHeaderClick = useCallback(
    (day) => {
      openChartWithMetrics([getDayRoasFieldName(day)])
    },
    [openChartWithMetrics],
  )

  const handleChartButtonClick = useCallback(() => {
    setChartInitialMetrics(
      getDefaultWeeklyChartMetrics({ readableRevenue, readableCost }),
    )
    setIsChartOpen(true)
  }, [readableRevenue, readableCost])

  const { data: ers, mutate: mutateEr } = useSWR([
    '/ers/weekly',
    { pid, startDate, endDate, country, ws, countries, packageName },
  ])

  const { data: revenues, mutate: mutateR } = useSWR([
    readableRevenue ? '/revenues/weekly' : null,
    { pid, startDate, endDate, country, ws, countries, packageName },
  ])

  const { data: costs, mutate: mutateC } = useSWR([
    readableCost ? '/costs/weekly' : null,
    { pid, startDate, endDate, country, ws, countries, packageName },
  ])

  const { data: dnu, mutate: mutateDnu } = useSWR([
    '/userActives/weekly',
    { pid, startDate, endDate, country, ws, countries, packageName },
  ])

  const weeklyRoasParams = {
    pid: pids,
    country,
    startDate,
    endDate: endDateForRoas,
    ws,
    countries,
    packageName,
  }

  const { data: day0RoasWeekly, mutate: mutateDay0Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 0 },
  ])

  const { data: day3RoasWeekly, mutate: mutateDay3Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 3 },
  ])

  const { data: day7RoasWeekly, mutate: mutateDay7Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 7 },
  ])

  const { data: day14RoasWeekly, mutate: mutateDay14Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 14 },
  ])

  const { data: day30RoasWeekly, mutate: mutateDay30Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 30 },
  ])

  const { data: day60RoasWeekly, mutate: mutateDay60Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 60 },
  ])

  const { data: day90RoasWeekly, mutate: mutateDay90Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 90 },
  ])

  const { data: day120RoasWeekly, mutate: mutateDay120Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 120 },
  ])

  const { data: day150RoasWeekly, mutate: mutateDay150Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 150 },
  ])

  const { data: day180RoasWeekly, mutate: mutateDay180Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 180 },
  ])

  const { data: day45RoasWeekly, mutate: mutateDay45Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 45 },
  ])

  const { data: day270RoasWeekly, mutate: mutateDay270Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 270 },
  ])

  const { data: day360RoasWeekly, mutate: mutateDay360Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 360 },
  ])

  const { data: day400RoasWeekly, mutate: mutateDay400Roas } = useSWR([
    readableRoas ? '/drs/weekly' : null,
    { ...weeklyRoasParams, day_n: 400 },
  ])

  const dayRoasWeeklyByDay = useMemo(
    () => ({
      0: day0RoasWeekly,
      3: day3RoasWeekly,
      7: day7RoasWeekly,
      14: day14RoasWeekly,
      30: day30RoasWeekly,
      45: day45RoasWeekly,
      60: day60RoasWeekly,
      90: day90RoasWeekly,
      120: day120RoasWeekly,
      150: day150RoasWeekly,
      180: day180RoasWeekly,
      270: day270RoasWeekly,
      360: day360RoasWeekly,
      400: day400RoasWeekly,
    }),
    [
      day0RoasWeekly,
      day3RoasWeekly,
      day7RoasWeekly,
      day14RoasWeekly,
      day30RoasWeekly,
      day45RoasWeekly,
      day60RoasWeekly,
      day90RoasWeekly,
      day120RoasWeekly,
      day150RoasWeekly,
      day180RoasWeekly,
      day270RoasWeekly,
      day360RoasWeekly,
      day400RoasWeekly,
    ],
  )

  const data = useMemo(() => {
    const weeks = getWeekRange(dayjs(String(startDate), 'YYYYMMDD'), ws, endDate)
    return weeks.map((week, idx) => {
      const currId = week.id
      const preId = weeks[idx + 1]?.id
      const row = {
        week,
        preWeek: weeks.find((w) => w.id === preId),
        costs: costs?.find((c) => c.id === currId),
        preCosts: costs?.find((c) => c.id === preId),
        revenues: revenues?.find((r) => r.id === currId),
        preRevenues: revenues?.find((r) => r.id === preId),
        er: ers?.[currId],
        preEr: ers?.[preId],
        dnu: dnu?.find((x) => x.id === currId),
        preDnu: dnu?.find((x) => x.id === preId),
      }

      WEEKLY_ROAS_DAYS.forEach((day) => {
        const dayData = dayRoasWeeklyByDay[day]
        row[getDayRoasFieldName(day)] = dayData?.[currId]
        row[getPrevDayRoasFieldName(day, 'weekly')] = dayData?.[preId]
      })

      return row
    })
  }, [
    startDate,
    endDate,
    ws,
    costs,
    revenues,
    ers,
    dnu,
    dayRoasWeeklyByDay,
  ])

  const columns = useMemo(() => {
    const pidForBaseline = pids.length === 1 ? pids[0] : null
    // 将产品子包传入标准查询，避免 Bus 继承原版标准。
    const packageNameForBaseline = hasProductPackages(pidForBaseline) && packageName !== 'total' ? packageName : null
    const rows = []
    rows.push(columnDate(country, drBaselines, pidForBaseline, costBaselines, profitBaselines, packageNameForBaseline))
    rows.push(columnDnu)
    if (readableCost) {
      rows.push(columnInstalls)
    }
    if (readableRevenue) {
      rows.push(columnRevenue)
    }
    if (readableCost) {
      rows.push(columnCosts(costBaselines, country, pidForBaseline))
    }
    if (readableRevenue && readableCost) {
      rows.push(columnProfit(profitBaselines, country, pidForBaseline))
    }
    rows.push(columnEr(erBaseLine))

    if (readableCost) {
      rows.push(columnCPI)
      rows.push(columnCPIInstalls)
    }

    if (readableRoas) {
      rows.push(
        ...createAllWeeklyDayRoasColumnsWithTd3(
          drBaselines,
          country,
          pidForBaseline,
          packageNameForBaseline,
          handleRoasHeaderClick,
        ),
      )
    }

    return rows
  }, [
    readableRevenue,
    readableCost,
    readableRoas,
    erBaseLine,
    drBaselines,
    costBaselines,
    profitBaselines,
    country,
    pids,
    packageName,
    handleRoasHeaderClick,
  ])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableMultiRowSelection: true,
    state: {
      columnFilters,
      columnVisibility,
    },
  })

  const getHideableColumnIds = useCallback(() => {
    return table
      .getAllLeafColumns()
      .filter((column) => column.getCanHide())
      .map((column) => column.id)
  }, [table])

  const handleViewModeChange = useCallback(
    (mode) => {
      const columnIds = getHideableColumnIds()
      const nextVisibility =
        mode === 'roas'
          ? buildWeeklyRoasModeVisibility(columnIds)
          : buildWeeklyNormalModeVisibility(columnIds)
      setColumnVisibility(() => nextVisibility)
      setViewMode(mode)
    },
    [getHideableColumnIds, setColumnVisibility],
  )

  const viewModeGroup = useMemo(
    () => ({
      value: viewMode,
      onValueChange: handleViewModeChange,
      options: WEEKLY_VIEW_MODE_OPTIONS,
    }),
    [viewMode, handleViewModeChange],
  )

  const refresh = () => [
    mutateR(),
    mutateC(),
    mutateEr(),
    mutateDnu(),
    mutateDay0Roas(),
    mutateDay3Roas(),
    mutateDay7Roas(),
    mutateDay14Roas(),
    mutateDay30Roas(),
    mutateDay60Roas(),
    mutateDay90Roas(),
    mutateDay120Roas(),
    mutateDay150Roas(),
    mutateDay180Roas(),
    mutateDay45Roas(),
    mutateDay270Roas(),
    mutateDay360Roas(),
    mutateDay400Roas(),
  ]

  const handleExportCsv = () => {
    const filename = `weekly_report_${dayjs().format('YYYYMMDDHHmmss')}.csv`
    const exportData = data.map((row) => {
      const newRow = {}
      // Week
      newRow.week =
        row.week.days?.length > 0
          ? `${row.week.days[0]}~${row.week.days[row.week.days.length - 1]}`
          : row.week.id || ''

      // Calculate days in week
      const daysInWeek = row.week.days?.length || 7

      // DNU
      newRow.dnu = formatNumber(row.dnu?.dnu, 0, 0)

      // Installs
      const installsValue = row.costs?.installs || 0
      newRow.installs = formatNumber(installsValue, 0, 0)
      const installsDaily = daysInWeek > 0 ? installsValue / daysInWeek : 0
      newRow['installs (daily avg)'] = formatNumber(installsDaily, 0, 0)

      // Revenues
      const revenuesValue = row.revenues?.total || 0
      newRow.revenues = formatUSD(revenuesValue, 2, 2)
      const revenuesDaily = daysInWeek > 0 ? revenuesValue / daysInWeek : 0
      newRow['revenues (daily avg)'] = formatUSD(revenuesDaily, 2, 2)

      // Costs
      const costsValue = row.costs?.costs || 0
      newRow.Costs = formatUSD(costsValue, 2, 2)
      const costsDaily = daysInWeek > 0 ? costsValue / daysInWeek : 0
      newRow['Costs (daily avg)'] = formatUSD(costsDaily, 2, 2)

      // Profit
      const profitValue = revenuesValue - costsValue
      newRow.Profit = formatUSD(profitValue, 2, 2)
      const profitDaily = daysInWeek > 0 ? profitValue / daysInWeek : 0
      newRow['Profit (daily avg)'] = formatUSD(profitDaily, 2, 2)

      // ER
      const erValue = (row.er || 0) / 100
      newRow.ER = formatPercent(erValue, 2, 2)

      // CPI(DNU)
      const cpiDnuValue = costsValue / (row.dnu?.dnu || 1)
      newRow['CPI(Dnu)'] = formatUSD(cpiDnuValue, 4, 4)

      // CPI(Installs)
      const cpiInstallsValue = costsValue / (installsValue || 1)
      newRow['CPI(Installs)'] = formatUSD(cpiInstallsValue, 4, 4)

      // ROAS
      WEEKLY_ROAS_DAYS.forEach((day) => {
        const exportKey = day === 0 ? 'ROAS' : `ROAS${day}`
        const roasVal = row[getDayRoasFieldName(day)]
        newRow[exportKey] = isWeeklyRoasValueVisible(roasVal)
          ? formatPercent(roasVal, 2, 2)
          : '-'
      })
      return newRow
    })
    exportToCsv(exportData, filename)
  }

  // 格式化完整数据的截止日期用于显示
  const lastCompleteDateStr = dayjs(lastCompleteDate.toString(), 'YYYYMMDD').format('YYYY-MM-DD')
  const lastCompleteDateForRoasStr = dayjs(
    lastCompleteDateForRoas.toString(),
    'YYYYMMDD',
  ).format('YYYY-MM-DD')

  return (
    <>
      <div className="flex items-center gap-4 py-4 sm:gap-2 sm:py-2 flex-wrap">
        <ToggleShowLine num={1} text="weekly sum" />
        <ToggleShowLine num={2} text="daily avg" />
        <ToggleShowLine num={3} text="weekly growth" />
        <ToggleShowLine num={4} text="absolute diff" />
        <Button onClick={refresh}>refresh</Button>
        <Button onClick={handleExportCsv}>Export</Button>
        <ChartCodeViewer
          open={isChartOpen}
          onOpenChange={setIsChartOpen}
          triggerButton={
            <Button variant="outline" onClick={handleChartButtonClick}>
              <LineChartIcon className="w-4 h-4" />
              Chart
            </Button>
          }
        >
          <WeeklyChart
            key={chartInitialMetrics.join(',')}
            data={data}
            initialMetrics={chartInitialMetrics}
            availableMetrics={availableMetrics}
          />
        </ChartCodeViewer>
        <span className="text-destructive text-nowrap xs:text-xs xs:text-wrap lg:text-base">
          Roas取的是total{latestBaselineDate && `(${latestBaselineDate})`}.
        </span>
        <DataTableViewOptions table={table} viewModeGroup={viewModeGroup} />
      </div>
      <div className="mb-2 px-1 text-sm text-muted-foreground">
        <span className="font-medium">数据说明：</span>
        已自动排除不完整日期的数据。一般数据（Revenue、Costs、ER、DNU）完整至{' '}
        <span className="font-medium">{lastCompleteDateStr}</span>
        （北京时间16:00后更新）， ROAS数据完整至{' '}
        <span className="font-medium">{lastCompleteDateForRoasStr}</span>
        （北京时间17:00后更新）。各周 ROAS/Dn 列需该周至少{' '}
        <span className="font-medium">7</span> 个有效 install 日才展示实绩。
      </div>
      <DataTableWithBorder table={table} />
    </>
  )
}

export const useShowDataStore = create((set) => ({
  line1: true,
  line2: true,
  line3: true,
  line4: false,
  toggleLine: (num) =>
    set((state) => ({ [`line${num}`]: !state[`line${num}`] })),
}))

const ToggleShowLine = ({ num, text }) => {
  const value = useShowDataStore((state) => state?.[`line${num}`])
  const toggleLine = useShowDataStore((state) => state.toggleLine)

  const handleChange = () => {
    toggleLine(num)
  }

  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={`line${num}`}
        checked={value}
        onCheckedChange={handleChange}
      />
      <label
        htmlFor="terms"
        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {text}
      </label>
    </div>
  )
}

export default WeekonWeek
